import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { fetchdropdownMall, fetchdropdownTenant, DropdownBrand } from "../services/dropdown";
import { DropdownOption } from "../types/dropdown";

interface RawDropdownData {
  DataId?: number;
  value?: number;
  DataLabel?: string;
  label?: string;
}

export default function useMallTenantDropdown(
  onTenantChange?: (tenantId: number, mallId: number) => void
) {
  const [mallOptions, setMallOptions] = useState<DropdownOption[]>([]);
  const [tenantOptions, setTenantOptions] = useState<DropdownOption[]>([]);
  const [brandOptions, setBrandOptions] = useState<DropdownOption[]>([]);

  const [selectedMall, setSelectedMall] = useState<number | null>(() => {
    const stored = localStorage.getItem("mall_id") || sessionStorage.getItem("mall_id");
    const num = stored ? Number(stored) : null;
    return num && !isNaN(num) ? num : null;
  });

  const [selectedTenant, setSelectedTenant] = useState<number | null>(() => {
    const stored = localStorage.getItem("tenant_id") || sessionStorage.getItem("tenant_id");
    const num = stored ? Number(stored) : null;
    return num && !isNaN(num) ? num : null;
  });

  const [selectedBrand, setSelectedBrand] = useState<number | null>(null);

  const isFirstMallRender = React.useRef(true);

  const storedTenantId = localStorage.getItem("tenant_id") || sessionStorage.getItem("tenant_id");
  const tenantFromStorage = React.useRef<boolean>(
    !!(storedTenantId !== null && storedTenantId !== undefined)
  );

  const onTenantChangeRef = React.useRef(onTenantChange);

  useEffect(() => {
    onTenantChangeRef.current = onTenantChange;
  });

  useEffect(() => {
    fetchdropdownMall(0)
      .then((res: { dropdownClassDatas?: RawDropdownData[]; data?: RawDropdownData[] }) => {
        const list = res?.dropdownClassDatas ?? res?.data ?? (Array.isArray(res) ? res : []);
        const opts: DropdownOption[] = (list as RawDropdownData[]).map((it) => ({
          value: Number(it.DataId ?? it.value ?? 0),
          label: String(it.DataLabel ?? it.label ?? ""),
        }));
        setMallOptions(opts);
      })
      .catch(() =>
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Failed to fetch mall options",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        })
      );
  }, []);

  useEffect(() => {
    if (mallOptions.length === 1 && !selectedMall) {
      setSelectedMall(mallOptions[0].value);
    }
  }, [mallOptions, selectedMall]);

  useEffect(() => {
    if (selectedMall) {
      localStorage.setItem("mall_id", String(selectedMall));
      sessionStorage.setItem("mall_id", String(selectedMall));

      if (isFirstMallRender.current) {
        isFirstMallRender.current = false;
      } else if (!tenantFromStorage.current) {
        setSelectedTenant(null);
        setSelectedBrand(null);
      }

      setTenantOptions([]);
      setBrandOptions([]);

      fetchdropdownTenant(selectedMall)
        .then((res: { dropdownClassDatas?: RawDropdownData[]; data?: RawDropdownData[] }) => {
          const list = res?.dropdownClassDatas ?? res?.data ?? (Array.isArray(res) ? res : []);
          const opts: DropdownOption[] = (list as RawDropdownData[]).map((it) => ({
            value: Number(it.DataId ?? it.value ?? 0),
            label: String(it.DataLabel ?? it.label ?? ""),
          }));
          setTenantOptions(opts);
        })
        .catch(() =>
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "error",
            title: "Failed to fetch tenant options",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          })
        );
    } else {
      localStorage.removeItem("mall_id");
      sessionStorage.removeItem("mall_id");
      setTenantOptions([]);
      setSelectedTenant(null);
      setBrandOptions([]);
      setSelectedBrand(null);
    }
  }, [selectedMall]);

  useEffect(() => {
    if (tenantOptions.length === 1 && !selectedTenant) {
      setSelectedTenant(tenantOptions[0].value);
    }
  }, [tenantOptions, selectedTenant]);

  useEffect(() => {
    if (selectedTenant === null || selectedTenant === undefined) {
      setBrandOptions([]);
      setSelectedBrand(null);
      return;
    }

    setBrandOptions([]);
    setSelectedBrand(null);

    localStorage.setItem("tenant_id", String(selectedTenant));
    sessionStorage.setItem("tenant_id", String(selectedTenant));

    if (selectedTenant === 0) {
      tenantFromStorage.current = false;
      onTenantChangeRef.current?.(selectedTenant, selectedMall ?? 0);
      return;
    }

    DropdownBrand(selectedTenant)
      .then((res: { dropdownClassDatas?: RawDropdownData[]; data?: RawDropdownData[] }) => {
        const list = res?.dropdownClassDatas ?? res?.data ?? (Array.isArray(res) ? res : []);
        const opts: DropdownOption[] = (list as RawDropdownData[]).map((it) => ({
          value: Number(it.DataId ?? it.value ?? 0),
          label: String(it.DataLabel ?? it.label ?? ""),
        }));
        setBrandOptions(opts);
        if (opts.length === 1) {
          setSelectedBrand(0);
        }
      })
      .catch(() =>
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Failed to fetch brand options",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        })
      );

    tenantFromStorage.current = false;
    onTenantChangeRef.current?.(selectedTenant, selectedMall ?? 0);
  }, [selectedTenant]);

  const handleSetSelectedMall = (mallId: number | null) => {
    tenantFromStorage.current = false;
    setSelectedMall(mallId);
  };

  const handleSetSelectedTenant = (tenantId: number | null) => {
    tenantFromStorage.current = false;
    setSelectedTenant(tenantId);
  };

  return {
    mallOptions,
    tenantOptions,
    brandOptions,
    selectedMall,
    selectedTenant,
    selectedBrand,
    setSelectedMall: handleSetSelectedMall,
    setSelectedTenant: handleSetSelectedTenant,
    setSelectedBrand,
  };
}